import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatBRL } from "../lib/api";
import { Building2, Loader2, AlertTriangle, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

export default function CapitalSourcePage() {
  const [socios, setSocios] = useState([]);
  const [socioId, setSocioId] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [executing, setExecuting] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, h] = await Promise.all([
          api.get("/socios"),
          api.get("/admin/capital-source/history"),
        ]);
        setSocios(Array.isArray(s.data) ? s.data : []);
        setHistory(Array.isArray(h.data) ? h.data : []);
      } catch (_) { /* noop */ }
    })();
  }, []);

  const loadPreview = async () => {
    if (!socioId) return;
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/capital-source/preview", { socio_id: socioId });
      setPreview(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao carregar preview");
    } finally {
      setLoading(false);
    }
  };

  const execute = async () => {
    if (reason.trim().length < 3) {
      toast.error("Motivo obrigatório (mín 3 caracteres)");
      return;
    }
    if (!window.confirm("Confirma a conversão? Esta ação é registrada em auditoria.")) return;
    setExecuting(true);
    try {
      const { data } = await api.post("/admin/capital-source/convert", {
        socio_id: socioId,
        reason: reason.trim(),
      });
      toast.success(`${data.contracts_converted} contrato(s) + ${data.installment_contracts_converted} parcelado(s)/acordo(s) convertidos`);
      setPreview(null);
      setReason("");
      // Refresh
      const h = await api.get("/admin/capital-source/history");
      setHistory(h.data || []);
    } catch (e) {
      toast.error("Falha ao executar conversão");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Administração</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Capital Próprio</h1>
        <p className="text-[#A3A3A3] text-sm mt-1 max-w-2xl">
          Converter contratos atribuídos a um sócio para "Capital Próprio da empresa".
          Capital próprio recebe 100% do lucro e capital recuperado, sem split com sócio.
        </p>
      </div>

      <div className="fc-card p-5 mb-5" data-testid="cs-preview-section">
        <h2 className="font-display text-[15px] font-semibold mb-3 flex items-center gap-2">
          <Building2 size={14} className="text-emerald-300"/> Conversão em massa
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
          <select
            className="fc-input sm:col-span-2"
            value={socioId}
            onChange={(e) => { setSocioId(e.target.value); setPreview(null); }}
            data-testid="cs-select-socio"
          >
            <option value="">Selecione o sócio...</option>
            {socios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button
            onClick={loadPreview}
            disabled={!socioId || loading}
            className="fc-btn-gold flex items-center justify-center gap-2"
            data-testid="cs-load-preview"
          >
            {loading ? <><Loader2 size={14} className="animate-spin"/> Carregando…</> : <>Gerar preview <ArrowRight size={13}/></>}
          </button>
        </div>

        {preview && (
          <div className="space-y-3" data-testid="cs-preview">
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex gap-2">
              <AlertTriangle size={14} className="text-amber-300 flex-shrink-0 mt-0.5"/>
              <div className="text-[12px] text-amber-200/90">
                <strong>Impacto:</strong> {preview.contracts_count} contrato(s) normais + {preview.installment_contracts_count} parcelado(s)/acordo(s) serão convertidos.
                A tesouraria do(s) sócio(s) será afetada conforme abaixo.
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12.5px]">
              <Stat label="Contratos" value={preview.contracts_count + preview.installment_contracts_count}/>
              <Stat label="Capital total" value={formatBRL(preview.capital_total)} accent/>
              <Stat label="Juros previstos" value={formatBRL(preview.juros_previstos_contratos)}/>
              <Stat label="Lucro pendente" value={formatBRL(preview.lucro_pendente_total)} accent/>
            </div>
            {preview.socio_impact?.length > 0 && (
              <div className="border-t border-[#1f1f1f] pt-3 mt-2">
                <div className="text-[10px] uppercase tracking-wider text-[#737373] mb-2">Impacto na tesouraria do sócio</div>
                {preview.socio_impact.map((g) => (
                  <div key={g.socio_id} className="flex items-center justify-between text-[12.5px] py-1.5 border-b border-[#141414] last:border-0">
                    <span className="text-white">{g.socio_name}</span>
                    <span className="text-[#A3A3A3]">
                      Capital na rua: <strong className="text-red-300">-{formatBRL(g.capital_na_rua)}</strong> ·
                      Lucro pendente: <strong className="text-red-300">-{formatBRL(g.lucro_pendente)}</strong>
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="border-t border-[#1f1f1f] pt-3">
              <label className="block mb-3">
                <span className="text-[11px] uppercase tracking-wider text-[#737373]">Motivo da conversão (obrigatório)</span>
                <textarea
                  className="fc-input mt-1"
                  rows="2"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex: regularização: estes contratos eram capital próprio cadastrados erroneamente como sócio Forte Capital"
                  data-testid="cs-reason"
                />
              </label>
              <div className="flex justify-end">
                <button
                  onClick={execute}
                  disabled={executing || !reason.trim()}
                  className="fc-btn-gold flex items-center gap-2 bg-emerald-500/10 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/20"
                  data-testid="cs-execute"
                >
                  {executing ? <><Loader2 size={14} className="animate-spin"/> Convertendo…</> : <><Check size={13}/> Executar conversão</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Histórico */}
      <div className="fc-card p-5" data-testid="cs-history">
        <h2 className="font-display text-[15px] font-semibold mb-3">Histórico de conversões</h2>
        {history.length === 0 ? (
          <div className="text-[12px] text-[#737373] py-3">Nenhuma conversão executada ainda.</div>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="border-b border-[#141414] pb-2.5">
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <div className="text-[13px] text-white">
                      {h.contract_ids.length} contrato(s) + {h.installment_contract_ids.length} parcelado(s)
                    </div>
                    <div className="text-[11px] text-[#737373] mt-0.5">{h.reason}</div>
                  </div>
                  <div className="text-[11px] text-[#737373] text-right flex-shrink-0">
                    {new Date(h.executed_at).toLocaleString("pt-BR")}<br/>
                    {h.executed_by_email}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="fc-card p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-[#737373]">{label}</div>
      <div className={`font-mono text-[15px] font-bold mt-0.5 ${accent ? "text-[#D4AF37]" : "text-white"}`}>{value}</div>
    </div>
  );
}
